import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { describeVoucherValue } from '@mlm/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@mlm/ui';
import { MemberPicker } from '../components/member-picker';
import {
  createVoucherProgram,
  deleteVoucherProgram,
  getTenantFeatures,
  issueVoucher,
  listIssuedVouchers,
  listVoucherPrograms,
  revokeVoucher,
  updateVoucherProgram,
  type AdminUserSearchResult,
  type IssuedVoucher,
  type VoucherProgram,
  type VoucherProgramInput,
} from '../lib/api';
import { useHasPerm } from '../lib/auth-store';
import { useProductSearch } from '../lib/use-product-search';
import { useProductsByIds } from '../lib/use-products-by-ids';

/**
 * Gift checks (cashier phase 4; internally `vouchers`). Two tabs:
 *
 *  - PROGRAMS: what a gift check takes off, how a member earns one (buying a
 *    number of chosen products, or by hand only) and how long it stays valid;
 *  - ISSUED: every gift check handed out, with issue-by-hand and revoke.
 *
 * Everything here is named by the tenant's `voucher_label` ("Gift Check").
 * While `vouchers_enabled` is off the page explains where to turn it on.
 */
export function GiftChecksPage() {
  const features = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  const label = features.data?.voucher_label?.trim() || 'Gift Check';
  const plural = label.endsWith('s') ? label : `${label}s`;

  if (features.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (!features.data?.vouchers_enabled) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold">{plural}</h1>
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            {plural} are turned off for this tenant. Switch them on in{' '}
            <Link
              to="/settings#gift-checks"
              className="text-primary underline-offset-2 hover:underline"
            >
              Settings, Gift checks
            </Link>{' '}
            to set up programs, show members their {plural.toLowerCase()} in the members area, and
            let the cashier apply one at checkout.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">{plural}</h1>
        <p className="text-sm text-muted-foreground">
          A {label} takes money off a member&apos;s order at the till, once. Members earn one by
          buying a number of chosen products, or you issue one by hand. They see theirs in the
          members area with a code and a QR.
        </p>
      </div>
      <Tabs defaultValue="programs">
        <TabsList>
          <TabsTrigger value="programs">Programs</TabsTrigger>
          <TabsTrigger value="issued">Issued</TabsTrigger>
        </TabsList>
        <TabsContent value="programs">
          <ProgramsTab label={label} />
        </TabsContent>
        <TabsContent value="issued">
          <IssuedTab label={label} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── programs ──────────────────────────────────────────────────────────

function ProgramsTab({ label }: { label: string }) {
  const qc = useQueryClient();
  const canWrite = useHasPerm('coupon.write');
  const programs = useQuery({ queryKey: ['voucher-programs'], queryFn: listVoucherPrograms });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['voucher-programs'] });

  const remove = useMutation({
    mutationFn: deleteVoucherProgram,
    meta: { successMessage: 'Program deleted.' },
    onSuccess: refresh,
  });

  return (
    <div className="space-y-4">
      {canWrite && !creating && <Button onClick={() => setCreating(true)}>+ New program</Button>}
      {creating && (
        <ProgramForm
          label={label}
          onClose={() => setCreating(false)}
          onSaved={() => {
            refresh();
            setCreating(false);
          }}
        />
      )}
      <Card>
        <CardHeader>
          <CardTitle>Programs</CardTitle>
        </CardHeader>
        <CardContent>
          {programs.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (programs.data?.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">
              No programs yet. Add the first one with “New program”.
            </p>
          ) : (
            <ul className="divide-y">
              {programs.data!.map((p) =>
                editing === p.id ? (
                  <li key={p.id} className="py-3">
                    <ProgramForm
                      label={label}
                      program={p}
                      onClose={() => setEditing(null)}
                      onSaved={() => {
                        refresh();
                        setEditing(null);
                      }}
                    />
                  </li>
                ) : (
                  <li key={p.id} className="flex items-center gap-3 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{p.name}</span>
                        <Tag>{describeVoucherValue(p)}</Tag>
                        {!p.active && <Tag>Inactive</Tag>}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {p.earnKind === 'purchase_count'
                          ? `Earned every ${p.earnCount} unit${p.earnCount === 1 ? '' : 's'} bought`
                          : EARN_KIND_LINE[p.earnKind]}
                        {' · '}
                        {p.appliesToAll ? 'Whole order' : `${p.productIds.length} product(s)`}
                        {' · '}
                        {p.validDays ? `Valid ${p.validDays} days` : 'Does not expire'}
                        {' · '}
                        {p.issuedCount} issued, {p.redeemedCount} used
                      </div>
                    </div>
                    {canWrite && (
                      <>
                        <Button variant="outline" size="sm" onClick={() => setEditing(p.id)}>
                          Edit
                        </Button>
                        {p.issuedCount === 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              if (confirm(`Delete "${p.name}"?`)) remove.mutate(p.id);
                            }}
                          >
                            Delete
                          </Button>
                        )}
                      </>
                    )}
                  </li>
                ),
              )}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Tag({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{children}</span>
  );
}

function ProgramForm({
  label,
  program,
  onClose,
  onSaved,
}: {
  label: string;
  program?: VoucherProgram;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(program?.name ?? '');
  const [description, setDescription] = useState(program?.description ?? '');
  const [discountType, setDiscountType] = useState<'percent' | 'amount'>(
    program?.discountType ?? 'percent',
  );
  const [discountValue, setDiscountValue] = useState(
    program ? String(Number(program.discountValue)) : '',
  );
  const [appliesToAll, setAppliesToAll] = useState(program?.appliesToAll ?? false);
  const [productIds, setProductIds] = useState<string[]>(program?.productIds ?? []);
  const [discountScope, setDiscountScope] = useState<'one_unit' | 'every_unit'>(
    program?.discountScope === 'every_unit' ? 'every_unit' : 'one_unit',
  );
  const [rewardUnitsCount, setRewardUnitsCount] = useState(program?.rewardUnitsCount ?? false);
  const [earnKind, setEarnKind] = useState<VoucherProgramInput['earnKind']>(
    program?.earnKind ?? 'purchase_count',
  );
  const [earnCount, setEarnCount] = useState(program?.earnCount ? String(program.earnCount) : '');
  const [earnSame, setEarnSame] = useState((program?.earnProductIds.length ?? 0) === 0);
  const [earnProductIds, setEarnProductIds] = useState<string[]>(program?.earnProductIds ?? []);
  const [validDays, setValidDays] = useState(program?.validDays ? String(program.validDays) : '');
  const [active, setActive] = useState(program?.active ?? true);

  const save = useMutation({
    mutationFn: () => {
      const payload: VoucherProgramInput = {
        name: name.trim(),
        description: description.trim() || null,
        discountType,
        discountValue: discountValue.trim(),
        appliesToAll,
        productIds: appliesToAll ? [] : productIds,
        discountScope,
        earnKind,
        earnCount: earnKind === 'purchase_count' ? Number(earnCount) : null,
        earnProductIds: earnKind === 'purchase_count' && !earnSame ? earnProductIds : [],
        rewardUnitsCount: earnKind === 'purchase_count' && rewardUnitsCount,
        validDays: validDays.trim() === '' ? null : Number(validDays),
        active,
      };
      return program ? updateVoucherProgram(program.id, payload) : createVoucherProgram(payload);
    },
    meta: { successMessage: 'Program saved.' },
    onSuccess: onSaved,
  });

  const err = (save.error as { response?: { data?: { error?: string } } } | null)?.response?.data
    ?.error;
  const byPurchase = earnKind === 'purchase_count';
  const countOk = !byPurchase || (Number.isInteger(Number(earnCount)) && Number(earnCount) >= 1);
  const canSave =
    name.trim() &&
    Number(discountValue) > 0 &&
    (appliesToAll || productIds.length > 0) &&
    countOk &&
    (!byPurchase || earnSame || earnProductIds.length > 0);

  return (
    <div className="rounded-md border bg-muted/30 p-4 space-y-4">
      <h3 className="text-base font-semibold">{program ? 'Edit program' : 'New program'}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Name</label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Buy 9, the 10th is on us"
            maxLength={80}
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Type</label>
            <Select
              value={discountType}
              onValueChange={(v) => setDiscountType(v as 'percent' | 'amount')}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="percent">% off</SelectItem>
                <SelectItem value="amount">Amount off</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              {discountType === 'percent' ? 'Percent (1–100)' : 'Amount'}
            </label>
            <Input
              value={discountValue}
              onChange={(e) => setDiscountValue(e.target.value)}
              placeholder={discountType === 'percent' ? '100' : '50'}
              inputMode="decimal"
            />
          </div>
        </div>
        <div className="space-y-1 md:col-span-2">
          <label className="text-xs text-muted-foreground">
            Description for members (optional)
          </label>
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="One free drink of any size"
            maxLength={300}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">
            Valid for (days, blank = no expiry)
          </label>
          <Input
            value={validDays}
            onChange={(e) => setValidDays(e.target.value)}
            type="number"
            min={1}
            placeholder="30"
          />
        </div>
        <label className="flex items-center gap-2 text-sm self-end pb-2">
          <Checkbox checked={active} onCheckedChange={(v) => setActive(v === true)} />
          Active (inactive programs issue nothing, but issued {label}s still work)
        </label>
      </div>

      <div className="space-y-2">
        <label className="text-xs font-semibold text-muted-foreground">Takes money off</label>
        <Choice
          options={[
            [true, 'The whole order'],
            [false, 'Selected products only'],
          ]}
          value={appliesToAll}
          onChange={setAppliesToAll}
        />
        {!appliesToAll && <ProductMultiPicker ids={productIds} onChange={setProductIds} />}
        <label className="block pt-1 text-xs font-semibold text-muted-foreground">
          How many items
        </label>
        <Choice
          options={[
            ['one_unit', 'One item'],
            ['every_unit', appliesToAll ? 'Every item' : 'Every matching item'],
          ]}
          value={discountScope}
          onChange={setDiscountScope}
        />
        <p className="text-xs text-muted-foreground">
          {discountScope === 'one_unit'
            ? `The single dearest ${appliesToAll ? 'item' : 'matching item'} in the sale, once, like "buy 9, the 10th is free".`
            : `Every ${appliesToAll ? 'item' : 'matching item'} in the sale, like a discount code.`}
        </p>
      </div>

      <div className="space-y-2">
        <label className="text-xs font-semibold text-muted-foreground">How members get one</label>
        <Choice
          options={[
            ['purchase_count', 'By buying products'],
            ['birthday', 'On their birthday'],
            ['topup', 'With a top-up'],
            ['manual', 'Issued by hand only'],
          ]}
          value={earnKind}
          onChange={setEarnKind}
        />
        {earnKind === 'birthday' && (
          <p className="text-xs text-muted-foreground">
            Each member claims one from their {label}s page on their birthday, once a year. How many
            days after the birthday a claim stays open, and whether KYC must be verified first, are
            set in Settings → Gift checks. Members need a birth date: ask for it under Settings →
            Custom registration fields.
          </p>
        )}
        {earnKind === 'topup' && (
          <p className="text-xs text-muted-foreground">
            Pick this program on a bonus bracket under Settings → Wallet top-up: every approved
            top-up in that bracket, online or at the till, gives the member one.
          </p>
        )}
        {earnKind === 'purchase_count' && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <span>One {label} for every</span>
              <Input
                value={earnCount}
                onChange={(e) => setEarnCount(e.target.value)}
                type="number"
                min={1}
                className="w-20"
                aria-label="Units per gift check"
              />
              <span>units bought of:</span>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={earnSame} onCheckedChange={(v) => setEarnSame(v === true)} />
              {appliesToAll
                ? 'Any product (every unit of a completed order counts)'
                : 'The same products it takes money off'}
            </label>
            {!earnSame && <ProductMultiPicker ids={earnProductIds} onChange={setEarnProductIds} />}
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={rewardUnitsCount}
                onCheckedChange={(v) => setRewardUnitsCount(v === true)}
              />
              An item a {label} paid for also counts toward the next one
            </label>
            <p className="text-xs text-muted-foreground">
              Units count when an order is completed, at the till or online. A void or refund takes
              them back; a {label} already issued stays with the member.
            </p>
          </div>
        )}
      </div>

      {err && <p className="text-sm text-destructive">{ERROR_COPY[err] ?? err}</p>}
      <div className="flex gap-2 pt-1">
        <Button disabled={!canSave || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : program ? 'Save' : 'Create'}
        </Button>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/** How a program that is not earned by buying gives its gift checks out. */
const EARN_KIND_LINE: Record<VoucherProgramInput['earnKind'], string> = {
  purchase_count: 'Earned by buying',
  manual: 'Issued by hand only',
  birthday: 'Claimed on the birthday, once a year',
  topup: 'Given with a top-up (Settings → Wallet top-up)',
};

const ISSUED_REASON_LABEL: Record<string, string> = {
  purchase_count: 'Earned',
  manual: 'By hand',
  birthday: 'Birthday',
  topup: 'Top-up',
};

const ERROR_COPY: Record<string, string> = {
  voucher_needs_products: 'Pick at least one product, or take money off the whole order.',
  voucher_needs_earn_count: 'Say how many units earn one.',
  voucher_percent_over_100: 'A percentage cannot be more than 100.',
  voucher_value_must_be_positive: 'The amount must be more than zero.',
  voucher_valid_days_invalid: 'Days must be a whole number of 1 or more, or blank.',
  voucher_program_in_use: 'Members hold gift checks of this program. Make it inactive instead.',
  voucher_program_inactive: 'This program is inactive. Make it active first.',
  voucher_not_revocable: 'Only an unused gift check can be revoked.',
  member_not_found: 'That member no longer exists.',
};

function Choice<T extends string | boolean>({
  options,
  value,
  onChange,
}: {
  options: Array<[T, string]>;
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(([v, text]) => (
        <button
          key={String(v)}
          type="button"
          onClick={() => onChange(v)}
          className={
            'rounded-md border px-3 py-1.5 text-sm ' +
            (value === v
              ? 'border-primary bg-primary/10 text-primary'
              : 'bg-background hover:bg-accent')
          }
        >
          {text}
        </button>
      ))}
    </div>
  );
}

/** Search-to-add product chips, the same shape as the Discounts page. */
function ProductMultiPicker({
  ids,
  onChange,
}: {
  ids: string[];
  onChange: (ids: string[]) => void;
}) {
  const [search, setSearch] = useState('');
  const { results, isLoading } = useProductSearch({ term: search, filter: { activeOnly: true } });
  const { products: selected } = useProductsByIds(ids);
  const toggle = (id: string) =>
    onChange(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  return (
    <div className="space-y-2">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 rounded border bg-muted/30 p-2">
          {selected.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => toggle(p.id)}
              title="Remove"
              className="flex max-w-full items-center gap-1 rounded-full border bg-background px-2 py-0.5 text-xs hover:bg-muted"
            >
              <span className="truncate">{p.name}</span>
              <span className="font-mono text-muted-foreground">{p.sku}</span>
              <span aria-hidden>×</span>
            </button>
          ))}
        </div>
      )}
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search products…"
        className="max-w-xs"
      />
      <div className="max-h-48 overflow-y-auto rounded border bg-background p-2 space-y-1">
        {results.map((p) => (
          <label key={p.id} className="flex items-center gap-2 text-sm py-0.5">
            <Checkbox checked={ids.includes(p.id)} onCheckedChange={() => toggle(p.id)} />
            <span className="truncate">{p.name}</span>
            <span className="text-xs text-muted-foreground font-mono">{p.sku}</span>
          </label>
        ))}
        {results.length === 0 && (
          <p className="text-xs text-muted-foreground">
            {isLoading ? 'Searching…' : 'No products match.'}
          </p>
        )}
      </div>
    </div>
  );
}

// ── issued ────────────────────────────────────────────────────────────

const STATUS_LABEL: Record<IssuedVoucher['status'], string> = {
  issued: 'Usable',
  redeemed: 'Used',
  expired: 'Expired',
  revoked: 'Revoked',
};

function IssuedTab({ label }: { label: string }) {
  const qc = useQueryClient();
  const canWrite = useHasPerm('coupon.write');
  const [status, setStatus] = useState<string>('all');
  const [programId, setProgramId] = useState<string>('all');
  const [q, setQ] = useState('');
  const programs = useQuery({ queryKey: ['voucher-programs'], queryFn: listVoucherPrograms });
  const params = {
    status: status === 'all' ? undefined : status,
    programId: programId === 'all' ? undefined : programId,
    q: q.trim() || undefined,
  };
  const issued = useQuery({
    queryKey: ['vouchers-issued', params],
    queryFn: () => listIssuedVouchers(params),
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['vouchers-issued'] });
    qc.invalidateQueries({ queryKey: ['voucher-programs'] });
  };

  const [issuing, setIssuing] = useState(false);
  const [member, setMember] = useState<AdminUserSearchResult | null>(null);
  const [issueProgram, setIssueProgram] = useState<string>('');
  const issue = useMutation({
    mutationFn: () => issueVoucher({ programId: issueProgram, userId: member!.id }),
    meta: { successMessage: `${label} issued.` },
    onSuccess: () => {
      refresh();
      setIssuing(false);
      setMember(null);
    },
  });
  const [revoking, setRevoking] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const revoke = useMutation({
    mutationFn: () => revokeVoucher(revoking!, reason.trim()),
    meta: { successMessage: `${label} revoked.` },
    onSuccess: () => {
      refresh();
      setRevoking(null);
      setReason('');
    },
  });
  const issueErr = (issue.error as { response?: { data?: { error?: string } } } | null)?.response
    ?.data?.error;
  const activePrograms = (programs.data ?? []).filter((p) => p.active);

  return (
    <div className="space-y-4">
      {canWrite &&
        (issuing ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Issue a {label} by hand</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 max-w-xl">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Member</label>
                <MemberPicker selected={member} onChange={setMember} disabled={issue.isPending} />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Program</label>
                <Select value={issueProgram} onValueChange={setIssueProgram}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Choose a program" />
                  </SelectTrigger>
                  <SelectContent>
                    {activePrograms.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} ({describeVoucherValue(p)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground">
                The member gets a notice in the members area with the code.
              </p>
              {issueErr && (
                <p className="text-sm text-destructive">{ERROR_COPY[issueErr] ?? issueErr}</p>
              )}
              <div className="flex gap-2">
                <Button
                  disabled={!member || !issueProgram || issue.isPending}
                  onClick={() => issue.mutate()}
                >
                  {issue.isPending ? 'Issuing…' : 'Issue'}
                </Button>
                <Button variant="outline" onClick={() => setIssuing(false)}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Button onClick={() => setIssuing(true)} disabled={activePrograms.length === 0}>
            + Issue by hand
          </Button>
        ))}

      <Card>
        <CardContent className="p-4 grid gap-3 md:grid-cols-[1fr_180px_220px]">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Member name, email, or code"
            aria-label="Search"
          />
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-9" aria-label="Status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {(Object.keys(STATUS_LABEL) as Array<IssuedVoucher['status']>).map((s) => (
                <SelectItem key={s} value={s}>
                  {STATUS_LABEL[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={programId} onValueChange={setProgramId}>
            <SelectTrigger className="h-9" aria-label="Program">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All programs</SelectItem>
              {(programs.data ?? []).map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {issued.isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Loading…</p>
          ) : (issued.data?.length ?? 0) === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">No {label}s match.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground border-b">
                <tr>
                  <th className="px-3 py-2">Code</th>
                  <th className="px-3 py-2">Member</th>
                  <th className="px-3 py-2">Program</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Issued</th>
                  <th className="px-3 py-2">Expires</th>
                  <th className="px-3 py-2">Used on</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {issued.data!.map((v) => (
                  <tr key={v.id} className="border-b last:border-0 align-top">
                    <td className="px-3 py-2 font-mono">{v.code}</td>
                    <td className="px-3 py-2">
                      <div>{v.memberName || v.memberEmail}</div>
                      {v.memberName && (
                        <div className="text-xs text-muted-foreground">{v.memberEmail}</div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div>{v.programName}</div>
                      <div className="text-xs text-muted-foreground">
                        {ISSUED_REASON_LABEL[v.issuedReason] ?? 'Earned'}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant={v.status === 'issued' ? 'default' : 'secondary'}>
                        {STATUS_LABEL[v.status]}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {new Date(v.issuedAt).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {v.expiresAt ? new Date(v.expiresAt).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {v.redeemedOrderCode ? (
                        <span className="font-mono">{v.redeemedOrderCode}</span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {canWrite && v.status === 'issued' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setRevoking(v.id);
                            setReason('');
                          }}
                        >
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {revoking && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Revoke this {label}?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 max-w-xl">
            <p className="text-sm text-muted-foreground">
              The member can no longer use it. This cannot be undone.
            </p>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (required)"
              maxLength={300}
            />
            <div className="flex gap-2">
              <Button
                variant="destructive"
                disabled={!reason.trim() || revoke.isPending}
                onClick={() => revoke.mutate()}
              >
                {revoke.isPending ? 'Revoking…' : 'Revoke'}
              </Button>
              <Button variant="outline" onClick={() => setRevoking(null)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
